import { useEffect, useState } from "react";
import { setTitle } from "../../util/useDocumentTitle";
import { format } from "../../helpers/format";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchSurvey, selectSurvey, updateSurvey } from "../../store/surveySlice";
import { setSurveyDetailsTab } from "../../store/uiSlice";
import { QuestionBrowser } from "../../components/survey/questionBrowser";
import { FormProvider, SubmitHandler, useForm, useWatch } from "react-hook-form";
import Survey from "../../models/survey";
import { Typeahead } from "react-bootstrap-typeahead";
import SurveyQuestion from "../../models/surveyQuestion";
import { api } from "../../store/api";

interface EditRouteParams {
  surveyId?: string;
}

export default function SurveyImportSettingsScreen() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //const ui = useAppSelector(state => state.ui.survey);
  const params = useParams<keyof EditRouteParams>();
  const surveyState = useAppSelector(selectSurvey);
  const survey = surveyState.survey;
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [sourceSurvey, setSourceSurvey] = useState<Survey>();
  const [sourceSurveyQuestions, setSourceSurveyQuestions] = useState<SurveyQuestion[]>();

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();

  const form = useForm<Survey>();
  const { register, handleSubmit, setValue, reset, formState: { errors }, control } = form;
  const onSubmit: SubmitHandler<Survey> = data => {
    // console.log(data);
    // console.log('selectedQuestionIds', selectedQuestionIds);
    const request: Survey = {
      ...sourceSurvey!,
    };

    console.log(request);
    setError(undefined);
    setIsSaving(true);
    api.survey(survey!.id).importSettings(request)
      .then((updated) => {
        setIsSaving(false);
        //setError(undefined);
        //const path = isEditing ? '' : '#created';
        //navigate(generatePath(ReviewDetailsRoute, { id: `${updated.id}` }) + path);
        dispatch(updateSurvey(updated));
        navigate('..');
      })
      .catch((reason) => {
        setError("Unable to import survey settings: " + reason);
        setIsSaving(false);
      });
  };

  useEffect(() => {
    if (params.surveyId) {
      const surveyId = parseInt(params.surveyId);
      if (!surveyState.isLoading && !surveyState.error) {
        if (survey === undefined || survey.id !== surveyId) {
          console.log('Fetching Survey', surveyId, survey);
          dispatch(fetchSurvey(surveyId));
        }
      }
    }
  }, [dispatch, params.surveyId, survey, surveyState.error, surveyState.isLoading]);



  // const watchIsActive = useWatch({
  //   control: control,
  //   name: 'isActive',
  // });

  // const questions = survey?.questions?.filter(q => q.type === "MC");
  useEffect(() => {
    api.surveys.list().then((surveys) => {
      setSurveys(surveys);
      //setIsLoading(false);
    })
      .catch((e: Error) => {
        console.error(e);
        setError("Unable to load surveys: " + e);
        //setIsLoading(false);
      });

  }, []);

  useEffect(() => {
    if (surveyState.isLoading) {
      setTitle(['Loading Survey Details...']);
    }
    else if (surveyState.survey) {
      setTitle([surveyState.survey.name, 'Import Settings', 'Surveys']);
      //reset(surveyState.survey);
    }
  }, [reset, surveyState.isLoading, surveyState.survey]);


  useEffect(() => {
    if (sourceSurvey) {
      api.surveys.get(sourceSurvey.id).then((result) => {
        setSourceSurveyQuestions(result.questions);
      })
        .catch((e: Error) => {
          console.error(e);
          setError("Unable to load source survey questions: " + e);
        });
    }
    else {
      setSourceSurveyQuestions(undefined);
    }
  }, [sourceSurvey]);

  const questionsWithoutMatchInSourceSurvey = survey?.questions?.filter(q => !sourceSurveyQuestions?.some(sq => sq.qualtricsId === q.qualtricsId));

  return (<div>
    <div className="d-flex align-items-center mb-3">
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item"><Link to='..'>Surveys</Link></li>
        {surveyState.isLoading && <li className="breadcrumb-item">Loading...</li>}
        {surveyState.survey && <li className="breadcrumb-item"><Link to='..'>{surveyState.survey.name}</Link></li>}
        <li className="breadcrumb-item active">Edit</li>
      </ol>
    </div>
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>

        <div className="row">
          <div className="col-md-3">
            <div className="card border-0 mb-4">
              <div className="card-header bg-none h6 p-3 m-0 d-flex align-items-center">
                Destination Survey
              </div>
              {survey && <div className="card-body row fw-bold">
                <div className="col-auto">
                  <h6 className="card-subtitle text-muted">Name</h6>
                  <p>{survey?.name}</p>
                </div>

                <div className="col-auto">
                  <h6 className="card-subtitle text-muted">Qualtrics ID</h6>
                  <div className="card-text mb-2">
                    {survey?.qualtricsId}
                  </div>
                </div>

                <div className="col-auto">
                  <h6 className="card-subtitle text-muted">Last Fetched</h6>
                  <div className="card-text mb-2">
                    {survey?.structureAsOfUtc && format.date(survey.structureAsOfUtc)}
                  </div>
                </div>

                <div className="col-auto">
                  <h6 className="card-subtitle text-muted"># Questions</h6>
                  <div className="card-text mb-2 text-end">
                    {format.number(survey.numberQuestions)}
                  </div>
                </div>
                <div className="col-auto">
                  <h6 className="card-subtitle text-muted"># Responses</h6>
                  <div className="card-text mb-2 text-end">
                    {format.number(survey.numberResponses)}
                  </div>
                </div>
              </div>}
            </div>


          </div>
          <div className="col-md-9">
            {surveyState.error && <div className="alert alert-danger"><strong>Error!</strong> {surveyState.error}</div>}
            <div className="card border-0 mb-4">
              <div className="card-header bg-none h6 p-3 m-0 d-flex align-items-center d-flex flex-row justify-content-between">
                <div><label>Import settings from:</label></div>
                <div><select className="form-control" placeholder="Select a survey" value={sourceSurvey?.id || ""} onChange={e => setSourceSurvey(surveys.find(s => s.id.toString() === e.target.value))}><option value=""> ** SELECT A SURVEY **</option>
                  {surveys?.filter(s => s.id !== survey?.id).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select></div>
              </div>
              {survey && sourceSurvey && <div className="card-body row fw-bold">

                <div className="col-12">
                  {sourceSurvey === undefined && <p>Which survey do you want to import the settings from? Select a Copy of this survey so that the Question IDs match.</p>}
                  {sourceSurvey && sourceSurveyQuestions && surveyState && <div>
                    <p>You're about to import settings from the survey <strong>{sourceSurvey.name}</strong>. This will copy the configuration from that survey into the current survey.</p>
                    <table className="table">
                      <thead>
                        <tr>
                          <th></th>
                          <th>Source Question</th>
                          <th>Destination Question</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sourceSurveyQuestions?.map(q => {
                          const destinationQuestion = surveyState.survey?.questions?.find(sq => sq.qualtricsId === q.qualtricsId);
                          return <tr key={q.id}>
                            <td>
                              {destinationQuestion ?
                                (q.domain ? <i className="fas fa-square-check text-success"></i> : <i className="fas text-dark"></i>)
                                : <i className="fas fa-square-xmark text-danger"></i>}
                            </td>
                          <td>
                            {q.text}
                            {q.domain && <div className="text-muted"><small>{q.domain} / {q.subDomain}</small></div>}
                          </td>
                          <td>{destinationQuestion?.text || "N/A"}

                          </td>
                        </tr>;
                        })}
                      </tbody>
                    </table>
                    </div>}
                    {(questionsWithoutMatchInSourceSurvey?.length ?? 0) > 0 && <div className="alert alert-warning"><strong>Warning!</strong> There are questions in the destination survey that do not have a match in the source survey.</div>}
                    {(questionsWithoutMatchInSourceSurvey?.length ?? 0) > 0 && <ul>
                      {questionsWithoutMatchInSourceSurvey!.map(q => <li key={q.id}>{q.text} <small className="text-muted">Qualtrics ID: {q.qualtricsId}</small></li>)}
                    </ul>}
                </div>
              </div>}
              <div className="card-footer">
                {error && <div className="alert alert-danger"><strong>Error</strong> {error}</div>}
                {isSaving === true && <button type="submit" className="btn btn-success" disabled>Importing Settings...</button>}
                {isSaving === false && <button type="submit" className="btn btn-success" disabled={sourceSurvey === undefined}>Import Settings</button>}
              </div>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  </div>);
}